/** Бланк звіту про внесок на підтримку працевлаштування осіб з інвалідністю — за формою,
 *  затвердженою наказом Мінфіну від 25.08.2026 № 453 (публікація на tax.gov.ua і
 *  zakon.rada.gov.ua; оригінал — import-sources/dps_eforms/j05/J0500701_blank_order453.docx).
 *  Одна форма і для юридичних осіб (електронний код J0500701), і для фізичних (F0500701):
 *  на папері коду форми немає.
 *
 *  Використовується РЕДАКТОРОМ (ч.1215: звіт на екрані виглядає як справжній бланк, поля
 *  вводу — жовті клітинки прямо у формі, як у декларації з ПДВ) і сторінкою ДРУКУ (ч.1213–1214:
 *  три аркуші, значення текстом). Що відтворено: сітка рядків 01–09 / 091–093 / розділів
 *  І–ІІІ з номерами виносок, клітинки «по знаку» (поштовий індекс, КВЕД, дата подання,
 *  податкові номери підписантів), блок підписів з М.П., виноски 1–18, блок «Заповнюють
 *  посадові особи податкового органу».
 *
 *  Значення реквізитів — з `/blank`: рівно ті поля, що йдуть у файл для ДПС. У бланку
 *  фізичної особи рядок 05 (ліквідований платник) і головний бухгалтер порожні. */
import type { CSSProperties, ReactNode } from 'react';
import type { DisabilityCategory, DisabilityReportBlank } from '@/api/disabilityContributionReports';
import { fmtMoney } from './SingleTaxBlank';

const B = '0.5pt solid #000';
const td: CSSProperties = { border: B, padding: '3px 5px', verticalAlign: 'middle' };
const no: CSSProperties = { ...td, textAlign: 'center', padding: '3px 2px' };
const val: CSSProperties = { ...td, textAlign: 'center', fontWeight: 700 };
const center: CSSProperties = { ...td, textAlign: 'center' };
const DASH = '—';

/** Сітка бланка: № · текст · 011 / підпис реквізиту · … · значення (частки ширини аркуша). */
const COLS = ['4%', '54.7%', '17%', '5.4%', '11.6%', '7.3%'];

const Sup = ({ children }: { children: ReactNode }) => (
  <sup style={{ fontSize: '0.68em', lineHeight: 0 }}>{children}</sup>
);

/** Значення на лінійках для заповнення, як у бланку (найменування, адреса). */
function Lined({ text, lines }: { text: string; lines: number }) {
  return (
    <div>
      <div style={{ borderBottom: B, textAlign: 'center', fontWeight: 700, minHeight: '1.2em' }}>{text}</div>
      {Array.from({ length: lines - 1 }, (_, i) => (
        <div key={i} style={{ borderBottom: B, height: '1.15em' }} />
      ))}
    </div>
  );
}

/** Клітинки «по знаку», як у бланку: по одному символу в клітинці. */
function CharCells({ chars, height }: { chars: string[]; height?: string }) {
  return (
    <div style={{ display: 'flex', height: height ?? '100%', minHeight: '1.3em' }}>
      {chars.map((ch, i) => (
        <div key={i} data-cell="" style={{
          flex: 1, borderLeft: i ? B : undefined, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontWeight: 700,
        }}>{ch}</div>
      ))}
    </div>
  );
}

/** Значення по клітинках зліва направо; коротше — решта клітинок порожні. */
export function charCells(value: string | undefined, count: number): string[] {
  const chars = [...(value ?? '').trim()];
  return Array.from({ length: Math.max(count, chars.length) }, (_, i) => chars[i] ?? '');
}

/** КВЕД у п'яти клітинках бланка: дві цифри, крапка (надрукована), дві цифри. */
export function kvedCells(kved: string | undefined): string[] {
  const m = /^(\d)(\d)\.(\d)(\d)?/.exec((kved ?? '').trim());
  return m ? [m[1]!, m[2]!, '.', m[3]!, m[4] ?? ''] : ['', '', '.', '', ''];
}

/** Дата подання в десяти клітинках: дд.мм.рррр, крапки надруковані в бланку. */
export function dateCells(hfill: string | undefined): string[] {
  const m = /^(\d)(\d)(\d)(\d)(\d)(\d)(\d)(\d)$/.exec(hfill ?? '');
  const d = m ? m.slice(1) : Array.from({ length: 8 }, () => '');
  return [d[0]!, d[1]!, '.', d[2]!, d[3]!, '.', d[4]!, d[5]!, d[6]!, d[7]!];
}

type Row = { no: string; code: string; label: ReactNode };

const CATEGORIES: Row[] = [
  { no: '091', code: 'R091G3', label: 'Платник внеску, у якого середньооблікова кількість штатних працівників облікового складу за квартал – від 8 до 25 працівників' },
  { no: '092', code: 'R092G3', label: 'Платник внеску, у якого середньооблікова кількість штатних працівників облікового складу за квартал – більше 25 працівників' },
  { no: '093', code: 'R093G3', label: 'Підприємство, установа, організація, основним видом діяльності якої є реабілітація осіб з інвалідністю, навчання таких осіб або догляд за ними' },
];
// Аркуш 1 бланка закінчується рядком 3 розділу І, аркуш 2 починається з рядка 4.
const SECTION_I_FIRST: Row[] = [
  { no: '1', code: 'R011G3', label: <>Середньооблікова кількість штатних працівників облікового складу за звітний період, працівників<Sup>11</Sup></> },
  { no: '2', code: 'R012G3', label: <>Зовнішні сумісники в еквіваленті повної зайнятості за звітний період<Sup>12</Sup></> },
  { no: '3', code: 'R013G3', label: 'Норматив робочих місць для працевлаштування осіб з інвалідністю, розрахований платником' },
];
const SECTION_I_REST: Row[] = [
  { no: '4', code: 'R014G3', label: <>Середньооблікова кількість штатних працівників – осіб з інвалідністю за звітний період<Sup>13</Sup></> },
  { no: '4.1', code: 'R0141G3', label: 'З них осіб з інвалідністю першої групи незалежно від причин її встановлення та/або осіб з інвалідністю другої групи з порушенням зору або психічними розладами' },
  { no: '5', code: 'R015G3', label: 'Різниця між нормативом робочих місць для працевлаштування осіб з інвалідністю і середньообліковою кількістю штатних працівників – осіб з інвалідністю за звітний період: (рядок 3 розділу І - (рядок 4 розділу І - рядок 4.1 розділу І) - рядок 4.1 х 2 )' },
];
const SECTION_II: Row[] = [
  { no: '1', code: 'R021G3', label: <>Нарахована заробітна плата (винагорода) за звітний період<Sup>12</Sup></> },
  { no: '2', code: 'R022G3', label: <>Середньомісячна заробітна плата (винагорода) у звітному періоді, розрахована на одного працівника<Sup>12</Sup></> },
  { no: '3', code: 'R023G3', label: <>Сума нарахованого внеску на підтримку працевлаштування осіб з інвалідністю за звітний період, який підлягає сплаті<Sup>14</Sup></> },
];
const SECTION_III: Row[] = [
  { no: '1', code: 'R031G3', label: <>Сума внеску, яка підлягала сплаті за звітний період<Sup>16</Sup></> },
  { no: '2', code: 'R032G3', label: 'Уточнена сума внеску, яка підлягає сплаті за звітний період, у якому виявлено помилку' },
  { no: '3', code: 'R033G3', label: 'Збільшення суми внеску, яка підлягає сплаті (рядок 2 розділу ІІІ - рядок 1 розділу ІІІ, якщо рядок 2 розділу ІІІ > рядок 1 розділу ІІІ)' },
  { no: '4', code: 'R034G3', label: <>Зменшення суми внеску, яка підлягає сплаті (рядок 2 розділу ІІІ - рядок 1 розділу ІІІ, якщо рядок 2 розділу ІІІ &lt; рядок 1 розділу ІІІ)<Sup>17</Sup></> },
  { no: '5', code: 'R035G3', label: <>Сума пені, яку нарахував платник самостійно відповідно статті 20<Sup>1</Sup> Закону, що підлягає сплаті<Sup>18</Sup></> },
];

const LAW = 'Закону України «Про основи соціальної захищеності осіб з інвалідністю в Україні»';
const FOOTNOTES: { n: number; body: ReactNode }[] = [
  { n: 1, body: 'Звіт, який подається за звітний період першим.' },
  { n: 2, body: 'Звіт, який подається наступним за звітний період до закінчення граничного строку подання.' },
  { n: 3, body: 'Звіт, який подається після закінчення граничного строку подання для відповідного звітного періоду.' },
  { n: 4, body: 'Зазначається звітний період, за який подається Звіт та обов’язково зазначається номер Звіту (в хронологічному порядку незалежно від типу Звіту), що подається за звітний період.' },
  { n: 5, body: 'Роботодавець / платник — роботодавець у значенні, наведеному у Законі України «Про основи соціальної захищеності осіб з інвалідністю в Україні». Набуває статус платника внеску на підтримку працевлаштування осіб з інвалідністю, у разі якщо середньооблікова кількість штатних працівників облікового складу за звітний період становить від 8 осіб і більше та водночас не виконано норматив робочих місць для працевлаштування осіб з інвалідністю.' },
  { n: 6, body: 'Зазначається код згідно з ЄДРПОУ роботодавця / платника або реєстраційний номер облікової картки платника податків — фізичної особи.' },
  { n: 7, body: 'Серію (за наявності) та номер паспорта зазначають фізичні особи, які через свої релігійні переконання відмовляються від прийняття реєстраційного номера облікової картки платника податків та офіційно повідомили про це відповідний контролюючий орган і мають відмітку в паспорті.' },
  { n: 8, body: 'Зазначається код згідно з ЄДРПОУ ліквідованого платника в разі подання правонаступником або головним підприємством за ліквідовану філію Звіту з типом «Уточнюючий» щодо уточнення показників нарахування, обчислення та сплати внеску на підтримку працевлаштування осіб з інвалідністю платником, якого ліквідовано.' },
  { n: 9, body: 'Зазначається код основного виду економічної діяльності (згідно з класифікацією видів економічної діяльності ДК 009:2010) або код статистичної класифікації виду економічної діяльності Європейського Співтовариства згідно з класифікацією NACE 2.1-UA.' },
  { n: 10, body: 'Заповнюється виключно платником. У разі подання Звіту роботодавцем рядки 091–093, 1–5 розділу І, 1–3 розділу ІІ, 1–5 розділу ІІІ прокреслюються (у разі подання Звіту у паперовій формі) або залишаються пустими (у разі подання Звіту в електронній формі), крім випадків подання Звіту з типом «Уточнюючий» роботодавцем, який є правонаступником або головним підприємством (за ліквідовану філію) щодо уточнення показників нарахування, обчислення та сплати внеску на підтримку працевлаштування осіб з інвалідністю платником, якого ліквідовано.' },
  { n: 11, body: 'Визначається за методикою, затвердженою центральним органом виконавчої влади, що забезпечує формування державної політики у сфері статистики. У разі здійснення розрахунку нормативу робочих місць для працевлаштування осіб з інвалідністю до середньооблікової кількості осіб штатного облікового складу не включаються штатні одиниці, що належать до робіт, професій з важкими, шкідливими чи небезпечними умовами праці, перелік яких затверджено постановою Кабінет Міністрів України від 1 квітня 2026 року № 490.' },
  { n: 12, body: 'Визначається з врахуванням вимог Порядку обчислення середньомісячної заробітної плати (винагороди) для розрахунку внеску на підтримку працевлаштування осіб з інвалідністю, затвердженого постановою Кабінету Міністрів України від 25 лютого 2026 року № 268.' },
  { n: 13, body: <>Розрахунок здійснюється з врахуванням вимог, визначених у статті 18<Sup>2</Sup> {LAW} щодо розміру оплати їх праці.</> },
  { n: 14, body: (
    <>
      Розраховується за формулою:<br />
      S = 40 % х рядок 2 розділу ІІ х N х рядок 5 розділу І, де: S — сума внеску на підтримку працевлаштування осіб з інвалідністю,<br />
      N — кількість місяців у кварталі.<br />
      <span style={{ display: 'block', textIndent: '1.5em' }}>На період дії воєнного стану в Україні та до останнього числа останнього місяця кварталу, в якому воєнний стан буде припинено чи скасовано, розрахунок здійснюється за формулою:</span>
      S = (40 % х рядок 2 розділу ІІ х N х рядок 5 розділу І) х 50 %.
    </>
  ) },
  { n: 15, body: 'Може бути заповнено винятково при поданні Звіту з типом «Уточнюючий».' },
  { n: 16, body: 'Дані рядка 3 розділу ІІ останнього Звіту з типом «Звітний» або «Звітний новий».' },
  { n: 17, body: 'Зазначається тільки як позитивне значення.' },
  { n: 18, body: <>За наявності підстав нараховує платник самостійно відповідно статті 20<Sup>1</Sup> {LAW}.</> },
];

const ColGroup = () => <colgroup>{COLS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>;
const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' };
const sectionHead: CSSProperties = { ...center, fontWeight: 700, padding: '7px 12px' };

function Footnotes({ from, to }: { from: number; to: number }) {
  return (
    <div style={{ fontSize: '9.5pt', textAlign: 'justify', lineHeight: 1.2 }}>
      {FOOTNOTES.filter((x) => x.n >= from && x.n <= to).map((x) => (
        <div key={x.n}><Sup>{x.n}</Sup> {x.body}</div>
      ))}
    </div>
  );
}

/** Редагування прямо в бланку (ч.1215): редактор передає свій стан і обробники. */
export interface DisabilityBlankEditor {
  readOnly: boolean;
  isPayer: boolean;
  isCorrection: boolean;
  /** Рядки бланка у стані редактора: {код поля: введене значення}. */
  values: Record<string, string>;
  setValue: (code: string, raw: string) => void;
  category: DisabilityCategory | null | undefined;
  setCategory: (c: DisabilityCategory) => void;
  /** Номер Звіту за період (ряд.02). */
  number: string;
  setNumber: (raw: string) => void;
  /** Податковий номер ліквідованого платника (ряд.05) — лише уточнюючий звіт юридичної особи. */
  liquidatedTin: string;
  setLiquidatedTin: (raw: string) => void;
  /** Підказка під полем: значення з обліку (differs — відрізняється від уведеного). */
  hint?: (code: string) => { text: string; differs: boolean } | null;
}

/** Поле вводу в клітинці бланка: жовте, без власної рамки (рамка — у клітинки). */
const editInput: CSSProperties = {
  width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', borderRadius: 0,
  background: '#fefce8', color: '#000', fontFamily: 'inherit', fontSize: 'inherit',
  fontWeight: 700, textAlign: 'center', padding: '2px 3px', margin: 0, height: 'auto',
};
const ROW_TITLES: Record<string, string> = { R01: 'Розділ І', R02: 'Розділ ІІ', R03: 'Розділ ІІІ' };

/** Сам бланк — окремо від завантаження даних (тестується без сервера).
 *  Без `editor` — друк: три аркуші, значення текстом. З `editor` — екран редактора:
 *  форма суцільна, поля вводу в клітинках, виноски згорнуті, без блоку податкового органу. */
export function DisabilityReportBlankSheets({ blank, editor }: {
  blank: DisabilityReportBlank; editor?: DisabilityBlankEditor;
}) {
  const screen = !!editor;
  const canEdit = !!editor && !editor.readOnly;
  const f = blank.fields;
  const isLegal = blank.form_code === 'J0500701';
  const payer = blank.is_payer;
  const mark = (code: string) => (f[code] ? 'X' : '');
  const text = (code: string) => f[code] ?? '';
  // Примітка 10: у неплатника рядки 091–093 і розділи І–ІІІ на папері прокреслюються.
  const categoryMark = (code: string) => (payer ? mark(code) : DASH);
  const intVal = (code: string) => f[code] ?? DASH;
  const moneyVal = (code: string) => (f[code] != null ? fmtMoney(f[code]) : DASH);
  // Підказка «з обліку» — службовий дрібний шрифт без зарубок; бланк завжди білий, тому кольори сталі.
  const hintOf = (code: string) => {
    const h = editor?.hint?.(code);
    if (!h) return null;
    return (
      <div data-hint={code} title="Значення, розраховане з обліку (кадри і проведені нарахування)"
        style={{ fontFamily: 'system-ui, sans-serif', fontSize: '8pt', fontWeight: 400, marginTop: 2,
          whiteSpace: 'nowrap', color: h.differs ? '#b45309' : '#64748b' }}>
        з обліку: {h.text}
      </div>
    );
  };
  const rowEditable = (code: string) => canEdit && (code.startsWith('R03') ? editor!.isCorrection : editor!.isPayer);
  const dataRow = (r: Row, value: string) => {
    const editable = rowEditable(r.code);
    const money = !r.code.startsWith('R01');
    return (
      <tr key={r.code}>
        <td style={no}>{r.no}</td>
        <td style={{ ...td, textAlign: 'justify', padding: '5px 8px 6px 14px' }} colSpan={3}>{r.label}</td>
        <td style={{ ...val, verticalAlign: 'top', padding: editable ? '3px 3px' : '5px 5px' }} colSpan={2} data-field={r.code}>
          {editable ? (
            <input type="number" className="no-spin" min={0} step={money ? 0.01 : 1} style={editInput}
              aria-label={`${ROW_TITLES[r.code.slice(0, 3)]}, рядок ${r.no}`}
              value={editor!.values[r.code] ?? ''} onChange={(e) => editor!.setValue(r.code, e.target.value)} />
          ) : value}
          {hintOf(r.code)}
        </td>
      </tr>
    );
  };
  const signer = (title: ReactNode, codeField: string, nameField: string, nameCaption: ReactNode) => (
    <tr>
      <td style={{ verticalAlign: 'top', width: '37.5%' }}>{title}</td>
      <td style={{ verticalAlign: 'top', width: '32.5%' }}>
        <div style={{ border: B }} data-field={codeField}><CharCells chars={charCells(f[codeField], 10)} height="1.45em" /></div>
        <div style={{ textAlign: 'center' }}>(Реєстраційний номер облікової картки платника податків або серія (за наявності) та номер паспорта<Sup>7</Sup>)</div>
      </td>
      <td style={{ verticalAlign: 'top', width: '10%', textAlign: 'center', paddingTop: '1.3em' }}>(підпис)</td>
      <td style={{ verticalAlign: 'top', width: '20%', textAlign: 'center' }}>
        <div style={{ fontWeight: 700, minHeight: '1.25em' }} data-field={nameField}>{text(nameField)}</div>
        <div>{nameCaption}</div>
      </td>
    </tr>
  );
  return (
    <div data-testid="disability-blank" style={{
      fontFamily: "'Times New Roman', Times, 'Liberation Serif', serif", fontSize: '10pt', lineHeight: 1.15, color: '#000',
      ...(screen ? { background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '22px 26px 26px',
        maxWidth: 860, margin: '0 auto' } : {}),
    }}>
      {/* ── аркуш 1 ── */}
      <div className="print-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2em' }}>
          <div style={{ textAlign: 'center', width: '22%', marginLeft: '1%' }}>Відмітка про отримання<br />(штамп податкового<br />органу)</div>
          <div style={{ width: '39.5%', fontSize: '11pt', lineHeight: 1.5 }}>ЗАТВЕРДЖЕНО<br />Наказ Міністерства фінансів України<br />25 серпня 2026 року № 453</div>
        </div>
        <table style={tableStyle}>
          <ColGroup />
          <tbody>
            <tr>
              <td style={no} rowSpan={3}>01</td>
              <td style={center} rowSpan={3}>Звіт про нарахування, обчислення та сплату внеску на підтримку працевлаштування осіб з інвалідністю</td>
              <td style={center}>011</td>
              <td style={td} colSpan={2}>Звітний<Sup>1</Sup></td>
              <td style={val} data-field="HZ">{mark('HZ')}</td>
            </tr>
            <tr>
              <td style={center}>012</td><td style={td} colSpan={2}>Звітний новий<Sup>2</Sup></td>
              <td style={val} data-field="HZN">{mark('HZN')}</td>
            </tr>
            <tr>
              <td style={center}>013</td><td style={td} colSpan={2}>Уточнюючий<Sup>3</Sup></td>
              <td style={val} data-field="HZU">{mark('HZU')}</td>
            </tr>
            <tr>
              <td style={{ ...td, padding: '16px 0 3px' }} colSpan={6}>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <div style={{ width: '1%' }} />
                  <div style={{ width: '33.8%', display: 'flex', border: B }}>
                    <div style={{ width: '19%', borderRight: B, textAlign: 'center', padding: '3px 2px' }}>02</div>
                    <div style={{ padding: '3px 6px' }}>Звітний період<Sup>4</Sup></div>
                  </div>
                  <div style={{ width: '4.2%' }} />
                  <div style={{ width: '24.8%', border: B, textAlign: 'center', fontWeight: 700, padding: '3px 2px' }} data-field="HZY">{text('HZY')}</div>
                  <div style={{ width: '4.2%' }} />
                  <div style={{ width: '12.1%', border: B, textAlign: 'center', fontWeight: 700, padding: '3px 2px' }} data-field="HZKV">{text('HZKV')}</div>
                  <div style={{ width: '2.8%' }} />
                  <div style={{ width: '16.4%', border: B, textAlign: 'center', fontWeight: 700, padding: canEdit ? 1 : '3px 2px' }} data-field="HNUM">
                    {canEdit ? (
                      <input aria-label="№ звіту за період" inputMode="numeric" style={editInput}
                        title="Порядковий номер звіту за цей квартал: перший поданий — 1, наступний (новий або уточнюючий) — 2. Після відмови ДПС номер зайнятий — ставте наступний."
                        value={editor!.number}
                        onChange={(e) => editor!.setNumber(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                    ) : text('HNUM')}
                  </div>
                </div>
                <div style={{ display: 'flex', marginTop: 3 }}>
                  <div style={{ width: '39%' }} />
                  <div style={{ width: '24.8%', textAlign: 'center' }}>(рік)</div>
                  <div style={{ width: '4.2%' }} />
                  <div style={{ width: '12.1%', textAlign: 'center' }}>(квартал)</div>
                  <div style={{ width: '2.8%' }} />
                  <div style={{ width: '16.4%', textAlign: 'center' }}>(номер Звіту)</div>
                </div>
              </td>
            </tr>
            <tr>
              <td style={no}>03</td>
              <td style={{ ...td, padding: 0 }} colSpan={5}>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <div style={{ width: '13.6%', borderRight: B, textAlign: 'center', padding: '2px 4px' }}>Роботодавець / платник<Sup>5</Sup></div>
                  <div style={{ flex: 1, padding: '5px 8px 6px' }} data-field="HNAME"><Lined text={text('HNAME')} lines={2} /></div>
                </div>
              </td>
            </tr>
            <tr>
              <td style={{ ...center, padding: '5px 12px' }} colSpan={6}>(повне найменування (прізвище (за наявності), ім’я, по батькові (за наявності) роботодавця / платника згідно з реєстраційними документами)</td>
            </tr>
            <tr>
              <td style={no}>04</td>
              <td style={td}>Податковий номер<Sup>6</Sup> або серія (за наявності) та номер паспорта<Sup>7</Sup> роботодавця / платника</td>
              <td style={{ ...val, verticalAlign: 'top' }} colSpan={4} data-field="HTIN">{text('HTIN')}</td>
            </tr>
            <tr>
              <td style={no}>05</td>
              <td style={td} colSpan={2}>Податковий номер ліквідованого платника<Sup>8</Sup></td>
              <td style={{ ...val, padding: canEdit && isLegal && editor!.isCorrection ? 1 : val.padding }} colSpan={3} data-field="HTIN1">
                {canEdit && isLegal && editor!.isCorrection ? (
                  <input aria-label="Податковий номер ліквідованого платника" inputMode="numeric" style={editInput}
                    title="Заповнюється лише правонаступником або головним підприємством, яке уточнює звіт ліквідованого платника"
                    value={editor!.liquidatedTin}
                    onChange={(e) => editor!.setLiquidatedTin(e.target.value.replace(/\D/g, '').slice(0, 10))} />
                ) : (isLegal ? text('HTIN1') : '')}
              </td>
            </tr>
            <tr>
              <td style={no} rowSpan={3}>06</td>
              <td style={{ ...td, verticalAlign: 'top', padding: '1px 3px 6px' }} rowSpan={3}>
                Податкова адреса роботодавця / платника
                <div style={{ width: '86%' }} data-field="HLOC"><Lined text={text('HLOC')} lines={3} /></div>
              </td>
              <td style={{ ...td, padding: '1px 3px' }}>Поштовий індекс</td>
              <td style={{ ...td, padding: 0, height: 1 }} colSpan={3} data-field="HZIP"><CharCells chars={charCells(f.HZIP, 5)} /></td>
            </tr>
            <tr>
              <td style={{ ...td, padding: '1px 3px' }}>Контактний номер телефону</td>
              <td style={{ ...val, verticalAlign: 'top' }} colSpan={3} data-field="HTEL">{text('HTEL')}</td>
            </tr>
            <tr>
              <td style={{ ...td, padding: '1px 3px' }}>Адреса електронної пошти</td>
              <td style={{ ...val, verticalAlign: 'top', wordBreak: 'break-all' }} colSpan={3} data-field="HEMAIL">{text('HEMAIL')}</td>
            </tr>
            <tr>
              <td style={{ ...no, verticalAlign: 'top' }} rowSpan={2}>07</td>
              <td style={{ ...td, padding: '4px 5px 6px' }} colSpan={5}>
                <div style={{ display: 'flex' }}>
                  <div style={{ whiteSpace: 'nowrap' }}>Звіт подається до</div>
                  <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, paddingRight: '14%' }} data-field="HSTI">{text('HSTI')}</div>
                </div>
              </td>
            </tr>
            <tr>
              <td style={center} colSpan={5}>(найменування податкового органу, до якого подається Звіт)</td>
            </tr>
            <tr>
              <td style={no}>08</td>
              <td style={td}>Код основного виду економічної діяльності / NACE<Sup>9</Sup></td>
              <td style={{ ...td, padding: 0, height: 1 }} colSpan={4} data-field="HKVED"><CharCells chars={kvedCells(f.HKVED)} /></td>
            </tr>
            <tr>
              <td style={no}>09</td>
              <td style={center} colSpan={3}>Категорія платника внеску<Sup>10</Sup></td>
              <td style={center} colSpan={2}>Відмітка</td>
            </tr>
            {CATEGORIES.map((r) => (
              <tr key={r.code}>
                <td style={{ ...no, verticalAlign: 'top', padding: '5px 2px' }}>{r.no}</td>
                <td style={{ ...td, padding: '5px 5px 7px' }} colSpan={3}>{r.label}</td>
                <td style={{ ...val, verticalAlign: 'top', padding: canEdit && editor!.isPayer ? 1 : '5px 5px', height: 1 }} colSpan={2} data-field={r.code}>
                  {canEdit && editor!.isPayer ? (
                    <label style={{ ...editInput, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                      height: '100%', minHeight: '2.4em', padding: '4px 3px', cursor: 'pointer' }}
                      title="Клацніть, щоб позначити категорію платника">
                      <input type="radio" name="disability-category" aria-label={`${r.no} категорія платника внеску`}
                        checked={editor!.category === r.no} onChange={() => editor!.setCategory(r.no as DisabilityCategory)}
                        style={{ position: 'absolute', opacity: 0, width: 1, height: 1, margin: 0 }} />
                      {editor!.category === r.no ? 'X' : '\u00a0'}
                    </label>
                  ) : categoryMark(r.code)}
                </td>
              </tr>
            ))}
            <tr>
              <td style={sectionHead} colSpan={4}>І. Розрахунок нормативу робочих місць для працевлаштування осіб з інвалідністю</td>
              <td style={{ ...center, padding: '3px 1px', fontSize: '9.5pt', whiteSpace: 'nowrap' }} colSpan={2}>Кількість працівників<br />(в цілих одиницях)</td>
            </tr>
            {SECTION_I_FIRST.map((r) => dataRow(r, intVal(r.code)))}
          </tbody>
        </table>
      </div>

      {/* ── аркуш 2 (на екрані — продовження тієї ж таблиці, без номера сторінки) ── */}
      <div className="print-page">
        {!screen && <div className="page-no" style={{ textAlign: 'center', marginBottom: '0.9em' }}>2</div>}
        <table style={screen ? { ...tableStyle, marginTop: '-0.5pt' } : tableStyle}>
          <ColGroup />
          <tbody>
            {SECTION_I_REST.map((r) => dataRow(r, intVal(r.code)))}
            <tr>
              <td style={sectionHead} colSpan={4}>ІІ. Обчислення та нарахування внеску на підтримку працевлаштування осіб з інвалідністю за звітний період</td>
              <td style={{ ...center, verticalAlign: 'top' }} colSpan={2}>(грн, коп.)</td>
            </tr>
            {SECTION_II.map((r) => dataRow(r, moneyVal(r.code)))}
            <tr>
              <td style={sectionHead} colSpan={4}>ІІІ. Визначення внеску на підтримку працевлаштування осіб з інвалідністю у зв’язку з виправленням помилок за попередні звітні періоди<Sup>15</Sup></td>
              <td style={{ ...center, verticalAlign: 'top' }} colSpan={2}>(грн, коп.)</td>
            </tr>
            {SECTION_III.map((r) => dataRow(r, moneyVal(r.code)))}
          </tbody>
        </table>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <div style={{ width: '63.8%', display: 'flex', border: B, borderTop: 'none' }}>
            <div style={{ width: '27.2%', borderRight: B, padding: '4px 5px' }}>Дата подання</div>
            <div style={{ flex: 1 }} data-field="HFILL"><CharCells chars={dateCells(f.HFILL)} /></div>
          </div>
          <div style={{ flex: 1, padding: '4px 0 4px 10px', whiteSpace: 'nowrap' }}>Наведена інформація є повною і достовірною.</div>
        </div>
        <table style={{ width: '98%', margin: '1.6em 1% 0', borderCollapse: 'collapse' }}>
          <tbody>
            {signer(<>Керівник (уповноважена особа)/<br />фізична особа</>, 'HKBOS', 'HBOS',
              <>(прізвище (за наявності), власне ім’я та по батькові (за наявності))</>)}
            <tr>
              <td />
              <td style={{ textAlign: 'right', padding: '0.5em 0 0' }}>М.П. (за наявності)</td>
              <td /><td />
            </tr>
            {signer(<>Головний бухгалтер<br />(особа, відповідальна за ведення бухгалтерського обліку)</>, 'HKBUH', 'HBUH',
              <>(прізвище (за наявності), власне ім’я та по батькові (за наявності))</>)}
          </tbody>
        </table>
      </div>

      {/* ── аркуш 3: виноски і блок податкового органу. На екрані виноски згорнуті, блоку немає. ── */}
      {screen ? (
        <details style={{ margin: '1.4em 1% 0' }}>
          <summary style={{ cursor: 'pointer', fontStyle: 'italic' }}>Виноски до бланка (1–18)</summary>
          <div style={{ marginTop: '0.5em' }}><Footnotes from={1} to={18} /></div>
        </details>
      ) : (
        <div className="print-page">
          <div className="page-no" style={{ textAlign: 'center', marginBottom: '0.9em' }}>3</div>
          <div style={{ margin: '0 1%' }}><Footnotes from={1} to={18} /></div>
          <div style={{ border: B, margin: '0.6em 0.4% 0', padding: '1.6em 1em 0.5em', textAlign: 'center', lineHeight: 1.6 }}>
            <div style={{ borderTop: '0.75pt dashed #000', width: '68%', margin: '0 auto 0.5em' }} />
            <div>Заповнюють посадові особи податкового органу</div>
            <div>Відмітка про внесення даних до електронної бази звітності «____» _______ 20___ року</div>
            <div style={{ borderBottom: B, width: '77%', margin: '1em auto 0' }} />
            <div>Посадова особа податкового органу (власне ім’я та прізвище)</div>
          </div>
        </div>
      )}
    </div>
  );
}

const INT_CODES = ['R011G3', 'R012G3', 'R013G3', 'R014G3', 'R0141G3', 'R015G3'];
const MONEY_CODES = ['R021G3', 'R022G3', 'R023G3'];
const CORRECTION_CODES = ['R031G3', 'R032G3', 'R033G3', 'R034G3', 'R035G3'];
/** Поля, якими на екрані керує редактор, а не збережений документ. */
const EDITOR_OWNED = new Set(['HZ', 'HZN', 'HZU', 'HZY', 'HZKV', 'HNUM', 'HTIN1']);

/** Бланк для екрана редактора: реквізити організації — з сервера (`/blank`, те, що піде у
 *  файл), а тип, номер, категорія і рядки — з поточного, ще не збереженого стану редактора. */
export function liveBlank(
  doc: { form_code: string; report_type: string; period_year: number; period_quarter: number; document_date?: string | null },
  server: DisabilityReportBlank | undefined,
  state: { values: Record<string, string>; isPayer: boolean; category?: DisabilityCategory | null; number: string; liquidatedTin?: string },
): DisabilityReportBlank {
  const fields: Record<string, string> = {};
  for (const [code, value] of Object.entries(server?.fields ?? {})) {
    if (code.startsWith('H') && !EDITOR_OWNED.has(code)) fields[code] = value;
  }
  const isCorrection = doc.report_type === 'CORRECTING';
  fields[isCorrection ? 'HZU' : doc.report_type === 'REPORTING_NEW' ? 'HZN' : 'HZ'] = '1';
  fields.HZY = String(doc.period_year);
  fields.HZKV = String(doc.period_quarter);
  if (state.number) fields.HNUM = state.number;
  if (!fields.HFILL && doc.document_date) {
    const [y, m, d] = doc.document_date.slice(0, 10).split('-');
    fields.HFILL = `${d}${m}${y}`;
  }
  const filled = (code: string) => state.values[code] != null && state.values[code] !== '';
  if (isCorrection) {
    if (doc.form_code === 'J0500701' && state.liquidatedTin) fields.HTIN1 = state.liquidatedTin;
    for (const code of CORRECTION_CODES) if (filled(code) && Number(state.values[code]) !== 0) fields[code] = state.values[code]!;
  }
  if (state.isPayer) {
    if (state.category) fields[`R${state.category}G3`] = '1';
    for (const code of [...INT_CODES, ...MONEY_CODES]) if (filled(code)) fields[code] = state.values[code]!;
  }
  return {
    form_code: doc.form_code as DisabilityReportBlank['form_code'], report_type: doc.report_type,
    period_year: doc.period_year, period_quarter: doc.period_quarter,
    is_payer: state.isPayer, fields, missing: server?.missing ?? [],
  };
}
